import React from "react";
import { useTheme } from "../lib/theme";

export function YubiKey3D({ state = "idle", className = "" }) {
  const { viz } = useTheme();
  const core =
    state === "reading" ? viz.amber[0] : state === "verified" ? viz.emerald[0] : state === "denied" ? viz.red[0] : viz.ledIdle;

  return (
    <svg viewBox="0 0 380 220" className={className} role="img" aria-label="hardware security key" data-testid="yubikey-3d">
      <defs>
        <linearGradient id="yk-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={viz.keyTop[0]} />
          <stop offset="42%" stopColor={viz.keyTop[1]} />
          <stop offset="100%" stopColor={viz.keyTop[2]} />
        </linearGradient>
        <linearGradient id="yk-side" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={viz.keySide[0]} />
          <stop offset="100%" stopColor={viz.keySide[1]} />
        </linearGradient>
        <linearGradient id="yk-metal" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={viz.keyMetal[0]} />
          <stop offset="38%" stopColor={viz.keyMetal[1]} />
          <stop offset="62%" stopColor={viz.keyMetal[2]} />
          <stop offset="100%" stopColor={viz.keyMetal[3]} />
        </linearGradient>
        <linearGradient id="yk-gold" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7a5a1a" />
          <stop offset="45%" stopColor="#e8c469" />
          <stop offset="100%" stopColor="#8d6a20" />
        </linearGradient>
        <linearGradient id="yk-sheen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={viz.keySheen[0]} />
          <stop offset="38%" stopColor={viz.keySheen[1]} />
          <stop offset="100%" stopColor={viz.keySheen[2]} />
        </linearGradient>
        <filter id="yk-glow" x="-260%" y="-260%" width="620%" height="620%">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="yk-shadow" x="-40%" y="-40%" width="200%" height="220%">
          <feGaussianBlur stdDeviation="12" />
        </filter>
        <clipPath id="yk-clip">
          <rect x="96" y="70" width="228" height="66" rx="30" />
        </clipPath>
      </defs>

      <ellipse cx="205" cy="176" rx="132" ry="13" fill={viz.keyShadow} filter="url(#yk-shadow)" />

      <g transform="rotate(-9 190 105)">
        {/* USB-C connector shell */}
        <rect x="34" y="86" width="70" height="34" rx="9" fill="url(#yk-metal)" />
        <rect x="34" y="86" width="70" height="34" rx="9" fill="url(#yk-sheen)" />
        <rect x="40" y="97" width="46" height="12" rx="6" fill={viz.keyHole} />
        <rect x="44" y="100" width="38" height="6" rx="3" fill="url(#yk-gold)" opacity="0.85" />

        {/* extruded body depth */}
        <rect x="96" y="82" width="228" height="66" rx="30" fill="url(#yk-side)" />
        {/* body top face */}
        <rect x="96" y="70" width="228" height="66" rx="30" fill="url(#yk-top)" />
        <rect x="96" y="70" width="228" height="66" rx="30" fill="url(#yk-sheen)" />
        <rect x="96.5" y="70.5" width="227" height="65" rx="29.5" fill="none" stroke={viz.keyEdge} strokeWidth="1" />

        {/* scanning beam while reading */}
        {state === "reading" && (
          <g clipPath="url(#yk-clip)">
            <rect x="96" y="70" width="46" height="66" fill={viz.amber[0]} opacity="0.2">
              <animate attributeName="x" values="96;278;96" dur="1.7s" repeatCount="indefinite" />
            </rect>
          </g>
        )}

        {/* NFC touch ring */}
        <g transform="translate(196 103)">
          <circle r="22" fill="none" stroke={viz.keyRing} strokeWidth="1.5" strokeDasharray="3 5" />
          <circle r="14" fill="none" stroke={viz.cyan[0]} strokeOpacity="0.4" strokeWidth="1.2" />
          <circle r="6" fill="none" stroke={viz.cyan[0]} strokeOpacity="0.7" strokeWidth="1.2" />
          {state === "reading" && (
            <circle r="8" fill="none" stroke={viz.amber[0]} strokeOpacity="0.8" strokeWidth="1.4">
              <animate attributeName="r" values="8;24" dur="1.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0" dur="1.5s" repeatCount="indefinite" />
            </circle>
          )}
        </g>

        {/* etched wordmark */}
        <text
          x="118"
          y="108"
          fill={viz.keyEtch}
          fontFamily="'JetBrains Mono', monospace"
          fontSize="11"
          letterSpacing="3.4"
        >
          VERALIX
        </text>

        {/* status LED */}
        <g transform="translate(268 103)">
          <circle r="12" fill={core} opacity="0.28" filter="url(#yk-glow)" />
          <circle r="6" fill={core} filter="url(#yk-glow)">
            {state !== "idle" && (
              <animate
                attributeName="opacity"
                values="1;0.35;1"
                dur={state === "verified" ? "2.4s" : "0.9s"}
                repeatCount="indefinite"
              />
            )}
          </circle>
          <circle r="6" fill="none" stroke={viz.keyEdge} strokeWidth="0.8" />
        </g>

        {/* keyring hole */}
        <circle cx="308" cy="103" r="7" fill={viz.keyHole} stroke={viz.keyEdge} strokeWidth="1" />
      </g>
    </svg>
  );
}
